import { FormikErrors, yupToFormErrors } from 'formik';
import i18next, { TFunction } from 'i18next';

import { AppState } from 'common/models/AppState';
import { makeAgreementsSchema } from 'common/forms/Agreements/schema';
import { makeBusinessPartnerInfoSchema } from 'common/forms/BusinessPartnerInfo/schema';
import { makeClientConfirmationSchema } from 'common/forms/ClientConfirmationForm/schema';
import { makeClientFormSchema } from 'common/forms/ClientForm/schema';
import { makeClientStatementSchema } from 'common/forms/ClientStatementForm/schema';
import { makeCompanyInfoSchema } from 'common/forms/CompanyInfo/schema';
import { makeConsultancyInitiationSchema } from 'common/forms/ConsultancyInitiationForm/schema';
import { makeConsultantConfirmationSchema } from 'common/forms/ConsultantConfirmationForm/schema';
import { makeFinanceAnalyseSchema } from 'common/forms/FinanceAnalyse/schema';
import { makeFinancesSchema } from 'common/forms/Finances/schema';
import { makeHazardProfileSchema } from 'common/forms/HazardProfile/schema';
import { makeInvestingGoalsSchema } from 'common/forms/InvestingGoalsForm/schema';
import { validateInvestingExperience } from 'common/forms/InvestingExperience/validate';
import { InvestingExperienceFormValues } from 'common/models/Forms';
import { makeMeetingSchema } from 'common/forms/MeetingForm/schema';
import { makeRequestInfoSchema } from 'common/forms/RequestInfoForm/schema';
import {
  selectAgreementBusinessPartnerInfo,
  selectAgreementMeeting,
  selectAgreementRequestInfo,
  selectInformationsCompanyInfo,
  selectInformationsCustomerInfo1,
  selectInformationsCustomerInfo2,
  selectInvestingAgreements,
  selectInvestingAnalyse,
  selectInvestingExperience,
  selectInvestingFinances,
  selectInvestingHazard,
  selectProtocolConsultantConfirmation,
  selectProtocolCustomerConfirmation,
  selectProtocolCustomerStatement,
  selectProtocolGoals,
  selectProtocolInitiation,
} from '../selectors';

export type FormStepFailure = {
  stepId: string;
  routeSegment: string;
  labelKey: string;
  errors: FormikErrors<unknown>;
};

export type FormValidationResult = {
  isValid: boolean;
  failures: FormStepFailure[];
};

type FormStepDefinition = {
  stepId: string;
  routeSegment: string;
  labelKey: string;
  isApplicable: (state: AppState) => boolean;
  selectValues: (state: AppState) => unknown;
  validateValues: (values: unknown, t: TFunction, state: AppState) => FormikErrors<unknown> | null;
};

function validateWithYupSchema(
  values: unknown,
  schemaFactory: (t: TFunction) => { validateSync: (value: unknown, options?: object) => unknown },
  t: TFunction,
  context?: object,
) {
  if (values === null || values === undefined) {
    return { _form: t('form.stepNotFilled') };
  }
  try {
    schemaFactory(t).validateSync(values, { abortEarly: false, context });
    return null;
  } catch (error) {
    return yupToFormErrors(error);
  }
}

function validateWithYupSchemaNoTranslate(
  values: unknown,
  schemaFactory: () => { validateSync: (value: unknown, options?: object) => unknown },
  t: TFunction,
) {
  if (values === null || values === undefined) {
    return { _form: t('form.stepNotFilled') };
  }
  try {
    schemaFactory().validateSync(values, { abortEarly: false });
    return null;
  } catch (error) {
    return yupToFormErrors(error);
  }
}

export const formStepDefinitions: FormStepDefinition[] = [
  {
    stepId: 'informations.companyInfo',
    routeSegment: 'informations/company-info',
    labelKey: 'form.validationSteps.companyInfo',
    isApplicable: () => true,
    selectValues: selectInformationsCompanyInfo,
    validateValues: (values, t) =>
      validateWithYupSchema(values, (translator) => makeCompanyInfoSchema(translator, i18next.language), t),
  },
  {
    stepId: 'informations.customerInfo1',
    routeSegment: 'informations/customer-info-1',
    labelKey: 'form.validationSteps.customerInfo1',
    isApplicable: () => true,
    selectValues: selectInformationsCustomerInfo1,
    validateValues: (values, t) => validateWithYupSchema(values, makeClientFormSchema, t),
  },
  {
    stepId: 'informations.customerInfo2',
    routeSegment: 'informations/customer-info-2',
    labelKey: 'form.validationSteps.customerInfo2',
    isApplicable: (state) => selectInformationsCustomerInfo2(state) !== null,
    selectValues: selectInformationsCustomerInfo2,
    validateValues: (values, t) => validateWithYupSchema(values, makeClientFormSchema, t),
  },
  {
    stepId: 'investing.experience',
    routeSegment: 'investing/experience',
    labelKey: 'form.validationSteps.experience',
    isApplicable: () => true,
    selectValues: selectInvestingExperience,
    validateValues: (values, t) => {
      if (values === null || values === undefined) {
        return { _form: t('form.stepNotFilled') };
      }
      const experienceValues = values as InvestingExperienceFormValues;
      const groupResults = experienceValues.table ?? [];
      const errors = validateInvestingExperience(experienceValues, groupResults, t);
      return Object.keys(errors).length > 0 ? errors : null;
    },
  },
  {
    stepId: 'investing.hazard',
    routeSegment: 'investing/hazard',
    labelKey: 'form.validationSteps.hazard',
    isApplicable: () => true,
    selectValues: selectInvestingHazard,
    validateValues: (values, t) => validateWithYupSchema(values, makeHazardProfileSchema, t),
  },
  {
    stepId: 'investing.finances',
    routeSegment: 'investing/finances',
    labelKey: 'form.validationSteps.finances',
    isApplicable: () => true,
    selectValues: selectInvestingFinances,
    validateValues: (values, t) => validateWithYupSchema(values, makeFinancesSchema, t),
  },
  {
    stepId: 'investing.analyse',
    routeSegment: 'investing/analyse',
    labelKey: 'form.validationSteps.analyse',
    isApplicable: () => true,
    selectValues: selectInvestingAnalyse,
    validateValues: (values, t) => validateWithYupSchema(values, makeFinanceAnalyseSchema, t),
  },
  {
    stepId: 'investing.agreements',
    routeSegment: 'investing/agreements',
    labelKey: 'form.validationSteps.agreements',
    isApplicable: () => true,
    selectValues: selectInvestingAgreements,
    validateValues: (values, t) => validateWithYupSchema(values, makeAgreementsSchema, t),
  },
  {
    stepId: 'protocol.goals',
    routeSegment: 'protocol/goals',
    labelKey: 'form.validationSteps.protocolGoals',
    isApplicable: () => true,
    selectValues: selectProtocolGoals,
    validateValues: (values, t) => validateWithYupSchema(values, makeInvestingGoalsSchema, t),
  },
  {
    stepId: 'protocol.initiation',
    routeSegment: 'protocol/initiation',
    labelKey: 'form.validationSteps.protocolInitiation',
    isApplicable: () => true,
    selectValues: selectProtocolInitiation,
    validateValues: (values, t) => validateWithYupSchema(values, makeConsultancyInitiationSchema, t),
  },
  {
    stepId: 'protocol.customerStatement',
    routeSegment: 'protocol/customer-statement',
    labelKey: 'form.validationSteps.customerStatement',
    isApplicable: () => true,
    selectValues: selectProtocolCustomerStatement,
    validateValues: (values, t) => validateWithYupSchema(values, makeClientStatementSchema, t),
  },
  {
    stepId: 'protocol.customerConfirmation',
    routeSegment: 'protocol/customer-confirmation',
    labelKey: 'form.validationSteps.customerConfirmation',
    isApplicable: () => true,
    selectValues: selectProtocolCustomerConfirmation,
    validateValues: (values, t) => validateWithYupSchema(values, makeClientConfirmationSchema, t),
  },
  {
    stepId: 'protocol.consultantConfirmation',
    routeSegment: 'protocol/consultant-confirmation',
    labelKey: 'form.validationSteps.consultantConfirmation',
    isApplicable: () => true,
    selectValues: selectProtocolConsultantConfirmation,
    validateValues: (values, t) => validateWithYupSchema(values, makeConsultantConfirmationSchema, t),
  },
  {
    stepId: 'agreement.meeting',
    routeSegment: 'agreement/meeting',
    labelKey: 'form.validationSteps.agreementMeeting',
    isApplicable: () => true,
    selectValues: selectAgreementMeeting,
    validateValues: (values, t) => validateWithYupSchemaNoTranslate(values, makeMeetingSchema, t),
  },
  {
    stepId: 'agreement.requestInfo',
    routeSegment: 'agreement/summary',
    labelKey: 'form.validationSteps.agreementRequestInfo',
    isApplicable: () => true,
    selectValues: selectAgreementRequestInfo,
    validateValues: (values, t) => validateWithYupSchema(values, makeRequestInfoSchema, t),
  },
  {
    stepId: 'agreement.businessPartnerInfo',
    routeSegment: 'agreement/summary',
    labelKey: 'form.validationSteps.agreementBusinessPartnerInfo',
    isApplicable: () => true,
    selectValues: selectAgreementBusinessPartnerInfo,
    validateValues: (values, t) => validateWithYupSchema(values, makeBusinessPartnerInfoSchema, t),
  },
];

export function validateEntireForm(state: AppState, t: TFunction): FormValidationResult {
  const failures: FormStepFailure[] = [];

  for (const step of formStepDefinitions) {
    if (!step.isApplicable(state)) {
      continue;
    }
    const values = step.selectValues(state);
    const errors = step.validateValues(values, t, state);
    if (errors && Object.keys(errors).length > 0) {
      failures.push({
        stepId: step.stepId,
        routeSegment: step.routeSegment,
        labelKey: step.labelKey,
        errors,
      });
    }
  }

  return {
    isValid: failures.length === 0,
    failures,
  };
}
